/**
 * Generic developmental/autism screening **result** data model — `BACKLOG.md` M8's explicit
 * licensing guardrail, structurally the same shape as `dermatology/patientAssessment.ts`'s "hard
 * guardrail, not a style note" header:
 *
 * **M-CHAT-R/F is free for a single practice to use, but a vendor distributing it across many
 * customer practices needs a separate license from the copyright holder (Diana Robins et al. /
 * mchatscreen.com), which this project does not have.** This module therefore stores only the
 * *outcome* of a screening a clinician administered off-system with their own licensed copy of the
 * real instrument — a tool name, a date, an optional raw score, and a risk-level classification the
 * clinician selects directly. **It contains none of the instrument's actual content**: no question
 * text, no answer options, and — just as important — **no computed scoring algorithm**. M-CHAT-R/F's
 * real interpretation bands (which raw-score ranges map to which risk level, and the specific
 * follow-up-question logic for the middle band) are themselves part of the copyrighted instrument,
 * not merely its question text, so `riskLevel` below is never *derived* from `score` anywhere in this
 * module or its callers — it is always a second, independent value the clinician enters directly,
 * exactly as they would when charting any other externally-administered, externally-scored
 * assessment. A hypothetical `score` → `riskLevel` mapping function is the one thing this module
 * must never grow.
 *
 * **ASQ-3 and PEDS are explicitly deferred** (`BACKLOG.md` M8: "licensed, pass-through fee") and
 * appear nowhere in `SCREENING_TOOLS` below — not even as an unimplemented placeholder — per the same
 * task brief's explicit instruction to leave them out entirely rather than guess at licensed content.
 * `'other'` is the escape hatch for a practice using a different tool this catalog does not name.
 */

export const SCREENING_TOOLS = ['mchat_r_f', 'other'] as const;
export type ScreeningTool = (typeof SCREENING_TOOLS)[number];

export function isScreeningTool(value: string): value is ScreeningTool {
  return (SCREENING_TOOLS as readonly string[]).includes(value);
}

/**
 * A clinician-entered classification, never computed. `notScreened` exists so a route/UI can
 * represent "no result recorded yet" as a real, honest value rather than treating `null`/absent as
 * "low risk" by default — the same "refuse rather than guess" discipline
 * `dermatology/destructionCharges.ts` uses for an undocumented lesion count, applied to the case
 * where a risk level is simply not yet known rather than not yet computable.
 */
export const SCREENING_RISK_LEVELS = ['low', 'medium', 'high'] as const;
export type ScreeningRiskLevel = (typeof SCREENING_RISK_LEVELS)[number];

export function isScreeningRiskLevel(value: string): value is ScreeningRiskLevel {
  return (SCREENING_RISK_LEVELS as readonly string[]).includes(value);
}

const SCREENING_TOOL_LABELS: Record<ScreeningTool, string> = {
  mchat_r_f: 'M-CHAT-R/F',
  other: 'Other',
};

/** Falls back to the raw value for anything unrecognised, matching `consentDocumentTypeLabel`'s own
 * reasoning — a route rendering a value this build does not recognise should show something rather
 * than crash the page. */
export function screeningToolLabel(value: string): string {
  return isScreeningTool(value) ? SCREENING_TOOL_LABELS[value] : value;
}

const SCREENING_RISK_LEVEL_LABELS: Record<ScreeningRiskLevel, string> = {
  low: 'Low risk',
  medium: 'Medium risk — follow-up indicated',
  high: 'High risk — refer',
};

export function screeningRiskLevelLabel(value: string): string {
  return isScreeningRiskLevel(value) ? SCREENING_RISK_LEVEL_LABELS[value] : value;
}
